import { useId } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Sun } from 'lucide-react';
import { useTranslation } from '../../i18n/useTranslation';
import { Alert } from '../atoms/Alert/Alert';
import { Badge } from '../atoms/Badge/Badge';
import { Button } from '../atoms/Button/Button';
import { Inline } from '../atoms/Inline/Inline';
import { Stack } from '../atoms/Stack/Stack';
import { Heading } from '../atoms/Typography/Heading';
import { Text } from '../atoms/Typography/Text';
import { VERDICT_TONE } from '../lighting/LightEvaluationCard';
import { getLatestLightEvaluation, latestLightEvaluationKey } from '../../services/lightSurveyService';

export interface LightingInsightSectionProps {
  aquariumId: string | undefined;
}

/**
 * Lighting part of the Reef Intelligence card: the newest AI lighting
 * evaluation (verdict, summary, the most important recommendation) with a link
 * to its light map. Nothing is shown before a survey was evaluated — the
 * lighting page explains how to start one.
 */
export function LightingInsightSection({ aquariumId }: LightingInsightSectionProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const headingId = useId();
  const query = useQuery({
    queryKey: latestLightEvaluationKey(aquariumId ?? ''),
    queryFn: () => getLatestLightEvaluation(aquariumId!),
    enabled: Boolean(aquariumId),
  });

  // Supplementary to the chemistry insight: while loading, on a load error (the
  // lighting page shows its own error state) and before any evaluation, the
  // card shows nothing extra rather than a second error next to its own.
  if (!aquariumId || query.isPending || query.isError) return null;

  const evaluation = query.data;
  if (!evaluation) return null;
  const { result } = evaluation;
  const top = [...result.recommendations].sort((a, b) => ['high', 'medium', 'low'].indexOf(a.priority) - ['high', 'medium', 'low'].indexOf(b.priority))[0];
  const measured = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }).format(new Date(evaluation.surveyMeasuredAt));

  return (
    <Stack gap="3" as="section" aria-labelledby={headingId} data-testid="reef-insight-lighting">
      <Inline gap="2" align="center">
        <Sun size={18} aria-hidden="true" />
        <Heading level={4} id={headingId}>{t('insights.lighting.title')}</Heading>
        <Badge tone={VERDICT_TONE[result.verdict]}>{t(`lighting.evaluation.verdict.${result.verdict}`)}</Badge>
      </Inline>
      <Text lang={evaluation.language}>{result.summary}</Text>
      {top && (
        <Text size="sm" lang={evaluation.language}>
          {t('insights.lighting.topRecommendation', { text: top.text })}
        </Text>
      )}
      <Text size="sm" color="muted">{t('insights.lighting.basedOn', { title: evaluation.surveyTitle, date: measured })}</Text>
      {evaluation.outdated && (
        <Alert tone="info" compact live="off">{t('insights.lighting.outdated')}</Alert>
      )}
      <Inline gap="2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(`/aquariums/${encodeURIComponent(aquariumId)}/lighting/${encodeURIComponent(evaluation.surveyId)}`)}
        >
          {t('insights.lighting.open')}
        </Button>
      </Inline>
    </Stack>
  );
}

export default LightingInsightSection;
